import React, { MutableRefObject, useMemo, useRef } from 'react';
import { GroupProps } from '@react-three/fiber/dist/declarations/src/three-types';
import { ConeBufferGeometry, CylinderBufferGeometry, Mesh, Quaternion, ShaderMaterial, Vector3 } from 'three';
import { Colors } from '../../../data/Colors';
import useUnitSphereGeometry from '../geometry/useUnitSphereGeometry';
import VectorLabel from './VectorLabel';
import { useFrame } from '@react-three/fiber';
import useDefaultValue from '../hooks/useDefaultValue';
import FlatColorMaterial from '../materials/FlatColorMaterial';
import { Vector3OrArray } from '../../../types';

const radius = 0.004;
const arrowHeight = 0.97;
const coneHeight = 0.031;
const cylHeight = arrowHeight - coneHeight;

const cylGeom = new CylinderBufferGeometry(radius, radius, cylHeight);
cylGeom.translate(0.0, cylHeight * .5, 0.0);
cylGeom.rotateX(Math.PI * .5);

const arrowGeom = new ConeBufferGeometry(0.012, coneHeight);
arrowGeom.translate(0, cylHeight + coneHeight * .5, 0);
arrowGeom.rotateX(Math.PI * .5);

type Props = GroupProps & {
    color?: number,
    dotSize?: number,
    target: Vector3OrArray,
    hideDot?: boolean,
    label?: string,
    alignLabel?: boolean,
    flipSide?: boolean,
    labelOffset?: Vector3OrArray
};

export default function SimpleVector({
  color,
  target,
  position,
  children,
  hideDot,
  dotSize,
  flipSide,
  alignLabel,
  label,
  ...props
}: Props): JSX.Element
{
  const sphereGeom = useUnitSphereGeometry();
  const materialRef = useRef<ShaderMaterial>(null) as MutableRefObject<ShaderMaterial>;
  const _dotSize = useDefaultValue(dotSize, 0.008);
  const { rotation, scale, arrowPos, spherePos } = useMemo(() =>
  {
    const q = new Quaternion();
    const p = new Vector3();
    const s = new Vector3(1, 1, 1);
    if (position && Array.isArray(position)) {
      p.set(position[0], position[1], position[2]);
    }
    else if (position instanceof Vector3) {
      p.copy(position);
    }

    const dir = new Vector3();
    if (Array.isArray(target)) {
      dir.x = target[0] - p.x;
      dir.y = target[1] - p.y;
      dir.z = target[2] - p.z;
    }
    else {
      dir.subVectors(target, p);
    }

    s.z = dir.length();

    dir.normalize();

    q.setFromUnitVectors(new Vector3(0, 0, 1), dir);
    return { rotation: q, scale: s, arrowPos: new Vector3(0, 0, s.z * (cylHeight) - cylHeight), spherePos: new Vector3(0, 0, s.z) };
  }, [ position, target ]);

  const labelRef = useRef<Mesh>();
  useFrame(() =>
  {
    const mesh = labelRef.current;
    if (!mesh) return;
  });

  return (<group {...props} position={position} quaternion={rotation}>
    <FlatColorMaterial color={color} color2={Colors.NeutralGrey} ref={materialRef}/>
    <mesh geometry={cylGeom} material={materialRef.current} scale={scale}/>
    <mesh geometry={arrowGeom} material={materialRef.current} position={arrowPos}/>
    {!hideDot &&
        <mesh geometry={sphereGeom} position={spherePos} scale={[ _dotSize, _dotSize, _dotSize ]} renderOrder={500}>
          <FlatColorMaterial color={color} color2={color} depthTest={false} depthWrite={false} />
        </mesh>
    }
    <group rotation={[Math.PI * .5, 0, 0]}>
      {
        label && <VectorLabel height={0.05} text={label} textColor={Colors.NeutralGrey} flipSide={flipSide}
          screenAlign={alignLabel} fontSize={20} labelOffset={[0.0, -0.03]} labelAlign={[0, 20]} bgAlpha={0} bgColor={Colors.NeutralGrey}/>
      }
    </group>
    {children}
  </group>);
}
